<template>
  <div class="changelog-entry">
    <div class="entry-header">
      <a-avatar
        size="small"
        class="entry-avatar"
        :class="{ system: !entry.user }"
      >
        {{ initials }}
      </a-avatar>
      <a-tooltip :title="entry.user?.email">
        <span class="entry-user">{{ entry.user?.username || 'System' }}</span>
      </a-tooltip>
      <a-tooltip :title="formatDate(entry.history_date)">
        <span class="entry-time">{{ fromNow(entry.history_date) }}</span>
      </a-tooltip>
      <a-tag v-if="entry.type === 'restored'" color="orange" class="ml-1">
        Restored
      </a-tag>
      <span v-if="entry.changes.length" class="entry-count">
        {{ entry.changes.length }}
        {{ entry.changes.length === 1 ? 'change' : 'changes' }}
      </span>
      <a-button
        v-if="!isLatest"
        type="link"
        size="small"
        class="restore-btn"
        @click="$emit('restore', entry)"
      >
        <template #icon><rollback-outlined /></template>
        Restore this version
      </a-button>
    </div>

    <p v-if="entry.type === 'created'" class="entry-note">
      Operation created with {{ entry.steps_count }} steps.
    </p>
    <p v-else-if="entry.type === 'initial'" class="entry-note">
      Earliest recorded version ({{ entry.steps_count }} steps). Changes made
      before change tracking began are not available.
    </p>
    <p v-else-if="entry.type === 'restored'" class="entry-note">
      Restored to the version from
      {{ formatDate(entry.restored_from?.history_date) }}.
    </p>

    <ul v-if="visibleChanges.length" class="change-list">
      <li
        v-for="change in visibleChanges"
        :key="change.field"
        class="change-row"
      >
        <span class="change-label">{{ change.label }}</span>

        <!-- Steps: added / removed / reordered -->
        <div v-if="isListKind(change)" class="change-body">
          <a-tag
            v-for="name in change.added"
            :key="'a-' + name"
            color="green"
            class="step-tag"
            >+ {{ name }}</a-tag
          >
          <a-tag
            v-for="name in change.removed"
            :key="'r-' + name"
            color="red"
            class="step-tag"
            >− {{ name }}</a-tag
          >
          <span v-if="change.reordered" class="change-hint">Order changed</span>
          <span v-if="change.regrouped" class="change-hint">
            Step groups changed
          </span>
          <span
            v-if="change.kind === 'step_list' && !change.new.length"
            class="change-hint"
            >(none selected now)</span
          >
          <span
            v-if="change.kind === 'steps' && change.old !== change.new"
            class="change-hint"
            >{{ change.old }} → {{ change.new }} steps</span
          >
        </div>

        <!-- Structured / long values: expandable -->
        <div v-else-if="isExpandableKind(change)" class="change-body">
          <span class="change-hint">Updated</span>
          <a-button
            type="link"
            size="small"
            class="p-0 ml-2"
            @click="toggle(change.field)"
          >
            {{ expanded[change.field] ? 'Hide details' : 'Show details' }}
          </a-button>
          <div v-if="expanded[change.field]" class="json-compare">
            <div>
              <div class="json-title">Before</div>
              <pre>{{ pretty(change.old) }}</pre>
            </div>
            <div>
              <div class="json-title">After</div>
              <pre>{{ pretty(change.new) }}</pre>
            </div>
          </div>
        </div>

        <!-- Scalars: old → new -->
        <div v-else class="change-body">
          <span class="old-value">{{ formatValue(change, change.old) }}</span>
          <arrow-right-outlined class="arrow" />
          <span class="new-value">{{ formatValue(change, change.new) }}</span>
        </div>
      </li>
    </ul>

    <p v-if="hiddenCount" class="entry-note muted">
      + {{ hiddenCount }} more {{ hiddenCount === 1 ? 'change' : 'changes' }}
      in other sections
    </p>
  </div>
</template>

<script>
import { ArrowRightOutlined, RollbackOutlined } from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { formatChangeValue, formatDateTime } from './changelog';

dayjs.extend(relativeTime);

export default {
  name: 'ChangelogEntry',
  components: { ArrowRightOutlined, RollbackOutlined },
  props: {
    entry: { type: Object, required: true },
    isLatest: { type: Boolean, default: false },
    group: { type: String, default: 'all' },
  },
  emits: ['restore'],

  data() {
    return { expanded: {} };
  },

  computed: {
    visibleChanges() {
      if (this.group === 'all') return this.entry.changes;
      return this.entry.changes.filter((c) => c.group === this.group);
    },
    hiddenCount() {
      return this.entry.changes.length - this.visibleChanges.length;
    },
    initials() {
      const name = this.entry.user?.username;
      return name ? name.slice(0, 2).toUpperCase() : 'SY';
    },
  },

  methods: {
    formatValue: formatChangeValue,

    isListKind(change) {
      return change.kind === 'steps' || change.kind === 'step_list';
    },
    isExpandableKind(change) {
      return change.kind === 'json' || change.kind === 'long_text';
    },
    toggle(field) {
      this.expanded = { ...this.expanded, [field]: !this.expanded[field] };
    },
    pretty(value) {
      if (value === null || value === undefined || value === '')
        return 'Not set';
      return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    },
    formatDate: formatDateTime,
    fromNow(value) {
      return dayjs(value).fromNow();
    },
  },
};
</script>

<style scoped>
.entry-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.entry-avatar {
  background-color: #1890ff;
  font-size: 11px;
}

.entry-avatar.system {
  background-color: #8c8c8c;
}

.entry-user {
  font-weight: 600;
  color: #262626;
}

.entry-time,
.entry-count {
  color: #8c8c8c;
  font-size: 13px;
}

.entry-count::before {
  content: '·';
  margin-right: 8px;
}

.restore-btn {
  margin-left: auto;
}

.entry-note {
  margin: 6px 0 0 32px;
  color: #595959;
  font-size: 13px;
}

.entry-note.muted {
  color: #8c8c8c;
  font-style: italic;
}

.change-list {
  list-style: none;
  margin: 8px 0 0 32px;
  padding: 0;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
}

.change-row {
  display: grid;
  grid-template-columns: minmax(160px, 240px) 1fr;
  gap: 12px;
  padding: 8px 12px;
  font-size: 13px;
}

.change-row + .change-row {
  border-top: 1px solid #f0f0f0;
}

.change-label {
  color: #595959;
  font-weight: 500;
}

.change-body {
  min-width: 0;
  word-break: break-word;
}

.old-value {
  color: #8c8c8c;
  text-decoration: line-through;
}

.new-value {
  color: #262626;
  font-weight: 600;
}

.arrow {
  margin: 0 8px;
  color: #bfbfbf;
  font-size: 11px;
}

.step-tag {
  margin-bottom: 4px;
}

.change-hint {
  color: #595959;
  margin-right: 8px;
}

.json-compare {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
}

.json-title {
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 4px;
}

.json-compare pre {
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 8px;
  max-height: 240px;
  overflow: auto;
  font-size: 12px;
  margin: 0;
  white-space: pre-wrap;
}

@media (max-width: 768px) {
  .change-row,
  .json-compare {
    grid-template-columns: 1fr;
  }

  .change-list,
  .entry-note {
    margin-left: 0;
  }
}
</style>
